import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft, TrendingUp, TrendingDown, Clock,
  RefreshCw, Search, X, ArrowUpDown
} from 'lucide-react';
import { AgentTrade } from '../../types';
import { apiService } from '../../services/apiService';
import { PnLBreakdown, pnlColor60, pnlColor30 } from '../../utils/pnl';
import { formatDateTime, formatCurrency } from '../../utils/format';
import { etDaysToExpiry, isAwaitingSettlement } from '../../utils/expiry';

interface AllOrdersPageProps {
  onBack: () => void;
  initialFilter?: 'all' | 'open' | 'closed';
}

type StatusFilter = 'all' | 'open' | 'closed';
type SortField = 'date' | 'symbol' | 'pnl';
type SortDir = 'asc' | 'desc';

// One row per agent trade (B-030: the page lists the agents' trades only; the viewer's own
// positions live in the Positions panel)
interface UnifiedTrade {
  id: string;
  symbol: string;
  strategy: string;
  strike: number;
  status: 'open' | 'closed';
  entryPrice: number;
  currentPrice: number | null;
  exitPrice: number | null;
  pnl: number;
  pnlPct: number | null;   // served ROI%; null when none was served (shown as "—", never 0)
  contracts: number;
  entryDate: string;
  exitDate: string | null;
  exitReason: string | null;
  isWin: boolean;
  expiration: string | null;
  agentEmoji: string | null;
  agentName: string | null;
  // CC stock leg
  stockEntryPrice?: number | null;
  stockExitPrice?: number | null;
  currentStockPrice?: number | null;
  // Backend-computed P&L breakdown (open positions)
  optionPnl?: number;
  stockPnl?: number;
  premiumPnlPct?: number;
}

const AllOrdersPage: React.FC<AllOrdersPageProps> = ({
  onBack,
  initialFilter = 'all',
}) => {
  const [agentClosedTrades, setAgentClosedTrades] = useState<AgentTrade[]>([]);
  const [agentOpenTrades, setAgentOpenTrades] = useState<AgentTrade[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(initialFilter);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [visibleCount, setVisibleCount] = useState(20);

  // Sync initialFilter prop when it changes (e.g. clicking different navbar tiles)
  useEffect(() => { setStatusFilter(initialFilter); setVisibleCount(20); }, [initialFilter]);

  const fetchAgentTrades = useCallback(async () => {
    try {
      setLoading(true);
      const [closed, open] = await Promise.all([
        apiService.getAgentTrades('closed').catch(() => []),
        apiService.getAgentTrades('open').catch(() => []),
      ]);
      setAgentClosedTrades(closed || []);
      setAgentOpenTrades(open || []);
    } catch (err) {
      console.error('Failed to fetch agent trades:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAgentTrades(); }, [fetchAgentTrades]);

  // Unify all trades into a single list
  const allTrades: UnifiedTrade[] = useMemo(() => {
    const trades: UnifiedTrade[] = [];

    // Agent open trades
    for (const t of agentOpenTrades) {
      const pos = t.position;
      trades.push({
        id: pos.id || `agent-open-${pos.symbol}-${pos.entry_date}`,
        symbol: pos.symbol,
        strategy: pos.strategy,
        strike: pos.strike,
        status: 'open',
        entryPrice: pos.entry_price,
        currentPrice: pos.current_price ?? null,
        exitPrice: null,
        pnl: pos.unrealized_pnl || 0,
        pnlPct: pos.unrealized_roi_pct ?? pos.unrealized_pnl_pct ?? null,
        contracts: pos.contracts,
        entryDate: pos.entry_date,
        exitDate: null,
        exitReason: null,
        isWin: (pos.unrealized_pnl || 0) >= 0,
        agentEmoji: t.agent.emoji,
        agentName: t.agent.name,
        expiration: pos.expiration || null,
        stockEntryPrice: pos.stock_entry_price,
        currentStockPrice: pos.current_stock_price,
        optionPnl: pos.option_pnl,
        stockPnl: pos.stock_pnl,
        premiumPnlPct: pos.premium_pnl_pct,
      });
    }

    // Agent closed trades
    for (const t of agentClosedTrades) {
      const pos = t.position;
      trades.push({
        id: pos.id || `agent-closed-${pos.symbol}-${pos.exit_date}`,
        symbol: pos.symbol,
        strategy: pos.strategy,
        strike: pos.strike,
        status: 'closed',
        entryPrice: pos.entry_price,
        currentPrice: null,
        exitPrice: pos.exit_price ?? null,
        pnl: pos.realized_pnl,
        pnlPct: pos.realized_roi_pct ?? null,
        contracts: pos.contracts,
        entryDate: pos.entry_date,
        exitDate: pos.exit_date ?? null,
        exitReason: pos.exit_reason ?? null,
        isWin: pos.realized_pnl >= 0,
        agentEmoji: t.agent.emoji,
        agentName: t.agent.name,
        expiration: pos.expiration || null,
        stockEntryPrice: pos.stock_entry_price,
        stockExitPrice: pos.exit_stock_price,
        // Backend-served realized leg breakdown (no client recompute)
        optionPnl: pos.option_pnl,
        stockPnl: pos.stock_pnl,
        premiumPnlPct: pos.realized_premium_pct ?? pos.premium_pnl_pct,
      });
    }

    return trades;
  }, [agentOpenTrades, agentClosedTrades]);

  // Filtered and sorted trades
  const filteredTrades = useMemo(() => {
    let result = allTrades;

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(t => t.status === statusFilter);
    }

    // Symbol search
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toUpperCase();
      result = result.filter(t =>
        t.symbol.toUpperCase().includes(q) ||
        (t.agentName && t.agentName.toUpperCase().includes(q)) ||
        t.strategy.toUpperCase().includes(q)
      );
    }

    // Sort
    result = [...result].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'date':
          cmp = new Date(a.exitDate || a.entryDate).getTime() - new Date(b.exitDate || b.entryDate).getTime();
          break;
        case 'symbol':
          cmp = a.symbol.localeCompare(b.symbol);
          break;
        case 'pnl':
          cmp = a.pnl - b.pnl;
          break;
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [allTrades, statusFilter, searchQuery, sortField, sortDir]);

  // Stats
  const agentTrades = allTrades;
  const totalOpen = agentTrades.filter(t => t.status === 'open').length;
  const totalClosed = agentTrades.filter(t => t.status === 'closed').length;
  const closedTrs = agentTrades.filter(t => t.status === 'closed');
  const wins = closedTrs.filter(t => t.isWin).length;
  const winRate = closedTrs.length > 0 ? ((wins / closedTrs.length) * 100) : 0;

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const stratLabel = (s: string) =>
    s === 'Cash-Secured Put' ? 'CSP' : s === 'Covered Call' ? 'CC' : s === 'SHARES' ? 'Shares' : s;

  return (
    <div className="min-h-[100dvh] bg-slate-950 pt-[56px]">
      {/* Header */}
      <div className="sticky top-[56px] z-40 bg-slate-950/95 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-400" />
            </button>
            <div className="flex-1">
              <h1 className="text-base font-bold text-white">All Orders</h1>
              <p className="text-11 text-slate-500">{totalOpen} open &middot; {totalClosed} closed</p>
            </div>
          </div>

          {/* Filters row */}
          <div className="flex items-center gap-2 mt-3">
            {/* Status pills */}
            <div className="flex items-center bg-slate-900/60 rounded-lg p-0.5 border border-slate-800">
              {(['all', 'open', 'closed'] as StatusFilter[]).map((f) => (
                <button
                  key={f}
                  onClick={() => setStatusFilter(f)}
                  className={`px-3 py-2 md:py-1 min-h-[44px] md:min-h-0 text-11 font-medium rounded-md transition-all ${
                    statusFilter === f
                      ? f === 'open' ? 'bg-amber-500/15 text-amber-400'
                        : f === 'closed' ? 'bg-emerald-500/15 text-emerald-400'
                        : 'bg-slate-700/50 text-white'
                      : 'text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {f === 'all' ? `All (${totalOpen + totalClosed})` : f === 'open' ? `Open (${totalOpen})` : `Closed (${totalClosed})`}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search symbol, agent, strategy..."
                className="w-full pl-8 pr-8 py-1.5 bg-slate-900/60 border border-slate-800 rounded-lg text-11 text-white placeholder-slate-600 focus:outline-none focus:border-slate-600 transition-colors"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2">
                  <X className="w-3.5 h-3.5 text-slate-500 hover:text-white" />
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="flex items-center gap-1">
              {([
                { field: 'date' as SortField, label: 'Date' },
                { field: 'symbol' as SortField, label: 'Symbol' },
                { field: 'pnl' as SortField, label: 'P&L' },
              ]).map(({ field, label }) => (
                <button
                  key={field}
                  onClick={() => toggleSort(field)}
                  className={`flex items-center gap-0.5 px-2 py-2 md:py-1 min-h-[44px] md:min-h-0 text-10 font-medium rounded transition-colors ${
                    sortField === field ? 'text-cyan-400 bg-cyan-500/10' : 'text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {label}
                  {sortField === field && (
                    <ArrowUpDown className={`w-3 h-3 ${sortDir === 'asc' ? 'rotate-180' : ''}`} />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* Stats row */}
        {/* mobile-ok: three compact stat tiles (percent / count values) */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl px-4 py-3">
            <div className="text-10 text-slate-500 uppercase tracking-wider mb-1">Win Rate</div>
            <div className="text-lg font-bold text-white">{winRate.toFixed(0)}%</div>
          </div>
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl px-4 py-3">
            <div className="text-10 text-slate-500 uppercase tracking-wider mb-1">Open</div>
            <div className="text-lg font-bold text-amber-400">{totalOpen}</div>
          </div>
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl px-4 py-3">
            <div className="text-10 text-slate-500 uppercase tracking-wider mb-1">Closed</div>
            <div className="text-lg font-bold text-emerald-400">{totalClosed}</div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20">
            <RefreshCw className="w-6 h-6 animate-spin text-slate-600" />
          </div>
        )}

        {/* Trade list */}
        {!loading && filteredTrades.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <Search className="w-10 h-10 text-slate-700 mb-3" />
            <p className="text-sm text-slate-500">
              {searchQuery ? 'No matching trades found' : 'No trades yet'}
            </p>
          </div>
        )}

        {!loading && filteredTrades.length > 0 && (
          <div className="space-y-1.5">
            {filteredTrades.slice(0, visibleCount).map((trade) => (
              <TradeRow key={trade.id} trade={trade} stratLabel={stratLabel} />
            ))}
            {filteredTrades.length > visibleCount && (
              <button
                onClick={() => setVisibleCount((c) => c + 20)}
                className="w-full py-3 text-11 font-medium text-cyan-400 hover:text-cyan-300 bg-slate-900/60 border border-slate-800 rounded-xl hover:border-cyan-500/30 transition-colors"
              >
                Show More ({filteredTrades.length - visibleCount} remaining)
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Trade Row Component
// ============================================================
const TradeRow: React.FC<{ trade: UnifiedTrade; stratLabel: (s: string) => string }> = ({ trade, stratLabel }) => {
  const isPos = trade.pnl >= 0;
  const strat = stratLabel(trade.strategy);
  const isCC = strat === 'CC';
  const isShares = trade.strategy === 'SHARES';
  const isClosed = trade.status === 'closed';
  const hasStockData = (isCC || isShares) && trade.stockEntryPrice != null && trade.stockEntryPrice > 0;

  const pnlCalc: PnLBreakdown | null = isClosed
    ? (() => {
        if (trade.exitPrice == null && !isShares) return null;
        // Render backend-served realized fields — no client recompute (backend is authoritative
        // on ROI%/premium%/leg P&L; see agent_service.enrich_position_pnl closed branch).
        return {
          optionPnl: trade.optionPnl ?? (isShares ? 0 : trade.pnl),
          stockPnl: trade.stockPnl ?? (isShares ? trade.pnl : 0),
          totalPnl: trade.pnl,
          totalPnlPct: trade.pnlPct,            // served realized_roi_pct
          premiumPnlPct: trade.premiumPnlPct ?? 0,  // served realized_premium_pct
          hasStockLeg: isShares || (hasStockData && trade.stockExitPrice != null && trade.stockExitPrice > 0),
        } as PnLBreakdown;
      })()
    : {
        optionPnl: trade.optionPnl ?? trade.pnl,
        stockPnl: trade.stockPnl ?? 0,
        totalPnl: trade.pnl,
        totalPnlPct: trade.pnlPct,
        premiumPnlPct: trade.premiumPnlPct ?? 0,
        hasStockLeg: isShares || (hasStockData && (trade.currentStockPrice || 0) > 0),
      } as PnLBreakdown;

  const displayPnl = pnlCalc ? pnlCalc.totalPnl : trade.pnl;
  const displayPnlPct = pnlCalc ? pnlCalc.totalPnlPct : trade.pnlPct;
  const displayIsPos = displayPnl >= 0;

  // Price displays
  const optionPriceDisplay = () => {
    if (isShares) return null; // No option price for SHARES
    const entry = `$${trade.entryPrice?.toFixed(2)}`;
    if (isClosed && trade.exitPrice != null) return `${entry} → $${trade.exitPrice.toFixed(2)}`;
    if (trade.status === 'open' && trade.currentPrice != null) return `${entry} → $${trade.currentPrice.toFixed(2)}`;
    return entry;
  };
  const stockPriceDisplay = () => {
    if (!hasStockData) return null;
    const entry = `$${trade.stockEntryPrice!.toFixed(2)}`;
    if (isClosed && trade.stockExitPrice) return `${entry} → $${trade.stockExitPrice.toFixed(2)}`;
    if (trade.status === 'open' && trade.currentStockPrice) return `${entry} → $${trade.currentStockPrice.toFixed(2)}`;
    return entry;
  };

  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-slate-900/60 border border-slate-800 rounded-xl hover:border-slate-700 transition-colors">
      {/* Left icon */}
      <div className="w-8 h-8 rounded-lg flex items-center justify-center text-base border border-slate-700/50 shrink-0 bg-slate-800">
        {trade.agentEmoji ? (
          <span>{trade.agentEmoji}</span>
        ) : trade.status === 'open' ? (
          <Clock className="w-4 h-4 text-yellow-400" />
        ) : isPos ? (
          <TrendingUp className="w-4 h-4 text-green-400" />
        ) : (
          <TrendingDown className="w-4 h-4 text-red-400" />
        )}
      </div>

      {/* Center info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-bold text-white font-mono">{trade.symbol}</span>
          <span className={`text-10 md:text-9 px-1.5 py-0.5 rounded font-medium ${
            strat === 'CSP' ? 'bg-blue-500/10 text-blue-400' : strat === 'Shares' ? 'bg-purple-500/10 text-purple-400' : 'bg-amber-500/10 text-amber-400'
          }`}>{strat}</span>
          {!isShares && <span className="text-10 text-slate-600">${trade.strike?.toFixed(0)}</span>}
          {trade.status === 'closed' && (
            <span className={`text-10 md:text-9 px-1.5 py-0.5 rounded font-bold ${displayIsPos ? 'bg-green-500/10 text-green-400 border border-green-500/15' : 'bg-red-500/10 text-red-400 border border-red-500/15'}`}>
              {displayIsPos ? 'WIN' : 'LOSS'}
            </span>
          )}
          {trade.status === 'open' && (
            <span className="text-10 md:text-9 px-1.5 py-0.5 rounded font-medium bg-yellow-500/10 text-yellow-400 border border-yellow-500/15">OPEN</span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-0.5 text-10 text-slate-500 flex-wrap">
          {trade.agentName && (
            <>
              <span>{trade.agentName}</span>
              <span className="text-slate-700">&middot;</span>
            </>
          )}
          {isShares ? (
            <span className="font-mono">{trade.contracts * 100} shares &middot; {stockPriceDisplay()}</span>
          ) : (
            <>
              <span className="font-mono">{trade.contracts}x &middot; Opt {optionPriceDisplay()}</span>
              {hasStockData && (
                <>
                  <span className="text-slate-700">&middot;</span>
                  <span className="font-mono">Stk {stockPriceDisplay()}</span>
                </>
              )}
            </>
          )}
          <span className="text-slate-700">&middot;</span>
          <span className="text-slate-500">Opened</span> <span>{formatDateTime(trade.entryDate)}</span>
          {isClosed && trade.exitDate && (
            <><span className="text-slate-700">&middot;</span><span className="text-slate-500">Closed</span> <span>{formatDateTime(trade.exitDate)}</span></>
          )}
          {trade.status === 'open' && (() => {
            if (isShares) return <><span className="text-slate-700">&middot;</span><span className="font-mono text-slate-600">No expiry</span></>;
            if (!trade.expiration) return null;
            const dte = etDaysToExpiry(trade.expiration);   // B-026: ET days, not the viewer's
            if (dte === null) return null;
            // B-030: a past expiration settles at the next exit cycle on the session's official
            // close (utils/expiry.ts rule 2) — it is awaiting settlement, never "-1d left"
            if (isAwaitingSettlement(trade.strategy, dte)) {
              return <><span className="text-slate-700">&middot;</span><span className="font-mono text-slate-300" title="Expired — settles at the next exit cycle on the expiration session's official close">Exp {trade.expiration.slice(5)} · Awaiting settlement</span></>;
            }
            return <><span className="text-slate-700">&middot;</span><span className={`font-mono ${dte <= 7 ? 'text-yellow-400' : ''}`}>Exp {trade.expiration.slice(5)} · {dte}d left</span></>;
          })()}
        </div>
        {/* P&L breakdown: Opt/Stk split */}
        {pnlCalc && !isShares && (pnlCalc.hasStockLeg || true) && (
          <div className="flex items-center gap-2 mt-0.5 text-10 md:text-9 font-mono">
            <span className={pnlColor60(pnlCalc.optionPnl)}>
              Opt:{pnlCalc.optionPnl >= 0 ? '+' : ''}{pnlCalc.optionPnl.toFixed(0)}
            </span>
            {pnlCalc.hasStockLeg && (
              <span className={pnlColor60(pnlCalc.stockPnl)}>
                Stk:{pnlCalc.stockPnl >= 0 ? '+' : ''}{pnlCalc.stockPnl.toFixed(0)}
              </span>
            )}
          </div>
        )}
        {isClosed && trade.exitReason && (
          <p className="text-10 text-slate-600 mt-0.5">{trade.exitReason}</p>
        )}
      </div>

      {/* Right P&L */}
      <div className="text-right shrink-0">
        <div className={`text-sm font-bold font-mono ${displayIsPos ? 'text-green-400' : 'text-red-400'}`}>
          {displayIsPos ? '+' : ''}{formatCurrency(displayPnl)}
        </div>
        <div className={`text-10 font-mono ${displayIsPos ? 'text-green-400/60' : 'text-red-400/60'}`}>
          {displayPnlPct == null ? '—' : `${displayPnlPct >= 0 ? '+' : ''}${displayPnlPct.toFixed(1)}%`} ROI
        </div>
        {pnlCalc && !isShares && (
          <div className={`text-10 md:text-9 font-mono ${pnlColor30(pnlCalc.premiumPnlPct)}`}>
            {pnlCalc.premiumPnlPct >= 0 ? '+' : ''}{pnlCalc.premiumPnlPct.toFixed(0)}%p
          </div>
        )}
      </div>
    </div>
  );
};

export default AllOrdersPage;
